import { FC, useEffect, useRef } from 'react';

import { useRecoilValue } from 'recoil';

import { createEnquiry } from '@shared/api/enquiry/enquiry.controller';
import {
  EnquiryCreate,
  EnquiryStatus,
  EnquiryType,
} from '@shared/api/enquiry/enquiry.interface';
import { postImage } from '@shared/api/uploadImage';
import { Button } from '@shared/components/Button';
import { EnquiryResponseTimeNotice } from '@shared/components/EnquiryResponseTimeNotice';
import { FileUploader } from '@shared/components/FileUploader';
import { LoadingModal } from '@shared/components/LoadingModal';
import TelField from '@shared/components/TelField';
import { TextField } from '@shared/components/TextField';
import { invalidateQueriesByUrlPart } from '@shared/hooks/recoil-query';
import { EnquiryInputs, IEnquiryInputs } from '@shared/interfaces/enquiry.interface';
import {
  notificateError,
  notificateSuccess,
} from '@shared/plugIn/ant-notification/ant-notifiaction';
import { defaultApiMutationOptions } from '@shared/plugIn/apiMutation';
import { notificationInstanceAtom } from '@shared/state/atom/notification.atom';
import { useAuth } from '@shared/state/hooks/useAuth';
import { checkFilesSize } from '@shared/utils/checkFilesSize';
import { SubmitHandler, useForm, useWatch } from 'react-hook-form';
import { useMutation, useQueryClient } from 'react-query';
import { useNavigate } from 'react-router-dom';

const ALLOWED_FILE_FORMAT = 'image/*';
const FILES_SIZE_LIMIT = 50000000; // 50mb

export interface EnquiryFormProps {
  //* 관리자 문의사항 관리의 탭(부고/스토어/셀러)을 가르는 값
  type: EnquiryType;
  //* 접수 성공 후 — 콜백이 있으면 그것(패널이 내역 탭으로 전환), 없으면 경로 이동
  onSuccess?: () => void;
  successTo?: string;
  //* 콘솔마다 상단 네비가 제목을 대신하는 곳은 숨긴다
  showTitle?: boolean;
  className?: string;
}

/**
 * 1:1 문의 폼 — 부고·화환 스토어·답례품 배송관리자·화환 판매자 콘솔이 같은 폼을 쓴다.
 * 콘솔별로 다른 건 문의 유형(type)과 접수 후 돌아갈 곳뿐이다.
 */
export const EnquiryForm: FC<EnquiryFormProps> = ({
  type,
  onSuccess,
  successTo = '/',
  showTitle = true,
  className = 'p-4',
}) => {
  const { userProfile, assetRole } = useAuth();
  const notifiacationInstance = useRecoilValue(notificationInstanceAtom);
  const navigate = useNavigate();

  const { register, handleSubmit, control, setValue, getValues } =
    useForm<IEnquiryInputs>();

  const telValue = useWatch({ control, name: EnquiryInputs.Tel, defaultValue: '' });

  //* 로그인 계정이 있으면 성함·연락처를 미리 채운다. 프로필이 처음 도착했을 때 1회만 시딩하고
  //* (창 포커스 refetch로 다시 흘러들어와 사용자가 고친 값을 덮어쓰지 않도록),
  //* 이미 입력한 칸은 건드리지 않는다. 채워진 뒤에도 그대로 수정 가능하다.
  const profileSeededRef = useRef(false);
  useEffect(() => {
    if (profileSeededRef.current) return;
    const name = userProfile?.info?.name ?? '';
    //* TelField는 숫자만 저장하고 표시할 때 하이픈을 붙인다 (setValue는 setValueAs를 안 타므로 직접 정리)
    const phone = (userProfile?.info?.phoneNumber ?? '').replace(/[^0-9]/g, '');
    if (!name && !phone) return;
    profileSeededRef.current = true;
    if (name && !getValues(EnquiryInputs.Name)) setValue(EnquiryInputs.Name, name);
    if (phone && !getValues(EnquiryInputs.Tel)) setValue(EnquiryInputs.Tel, phone);
  }, [userProfile, setValue, getValues]);

  // loding modal open state
  const queryClient = useQueryClient();
  const { isLoading: processingEnquiry, mutateAsync } = useMutation(
    defaultApiMutationOptions,
    {
      onSuccess: () => {
        void invalidateQueriesByUrlPart(queryClient, 'enquiry');
      },
    },
  );

  const onSubmit: SubmitHandler<IEnquiryInputs> = async (data) => {
    const isAllowed = checkFilesSize(data.files, FILES_SIZE_LIMIT);

    if (isAllowed) {
      // Post Image
      let imageId: string | null = null;
      if (data.files[0]) {
        const image = await postImage(data.files[0], assetRole());
        imageId = image._id;
      }

      const enquiryCreateDto: EnquiryCreate = {
        user: userProfile?._id ?? '',
        type,
        name: data.name,
        phoneNumber: data.tel,
        title: data.title,
        content: data.description,
        image: imageId ?? null,
        status: EnquiryStatus.Todo,
      };

      const onEnquiryCreate = async () => {
        const createdEnquiry = await createEnquiry(enquiryCreateDto);

        if (createdEnquiry) {
          notificateSuccess(notifiacationInstance, '문의가 정상적으로 요청되었습니다.');
          if (onSuccess) onSuccess();
          else navigate(successTo);
        } else {
          notificateError(
            notifiacationInstance,
            '문의가 정상적으로 요청되지 않았습니다.',
          );
        }
      };

      mutateAsync(onEnquiryCreate());
    } else {
      // 파일 크기가 초과됨.
      notificateError(notifiacationInstance, '파일의 크기를 줄여주세요.');
    }
  };

  return (
    <>
      <div className={className}>
        {showTitle && <h1 className="py-8 text-center text-xl font-bold">1:1 문의</h1>}
        <div className={showTitle ? '-mt-4 pb-6' : 'pb-6'}>
          <EnquiryResponseTimeNotice />
        </div>
        {/* Inputs */}
        <form onSubmit={handleSubmit(onSubmit)} className="space-y-6">
          {/* Text Inputs */}
          <TextField
            type="text"
            label="성함"
            placeholder="성함을 입력해주세요."
            className="w-full text-sm"
            labelClassname="pl-2 pb-1 font-bold"
            {...register(EnquiryInputs.Name, { required: true })}
          />
          <TelField
            label="연락처"
            labelClassname="pl-2 pb-1 font-bold"
            value={telValue}
            register={register}
            name={EnquiryInputs.Tel}
            className="w-full text-sm"
            required={true}
          />
          <TextField
            type="text"
            label="제목"
            placeholder="제목을 입력해주세요."
            className="w-full text-sm"
            labelClassname="pl-2 pb-1 font-bold"
            {...register(EnquiryInputs.Title, { required: true })}
          />
          {/* Textarea Input */}
          <div>
            <div className="label-col">
              <p className="pl-2 pb-1 font-bold">문의 내용</p>
            </div>
            <div className="relative">
              <textarea
                placeholder="문의 내용을 입력해주세요."
                className="textfield h-36 w-full py-3 text-sm"
                {...register(EnquiryInputs.Description, { required: true })}
              />
            </div>
          </div>
          {/* File Input */}
          <div>
            <div className="label-col">
              <p className="pl-2 font-bold">첨부파일</p>
              <span className="inline-block pl-2 pb-6 text-sm">
                최대 50mb까지 업로드 가능합니다.
              </span>
            </div>
            <div className="relative">
              <FileUploader
                multiple
                accept={ALLOWED_FILE_FORMAT}
                {...register(EnquiryInputs.Files)}
              />
            </div>
          </div>
          {/* Submit Button */}
          <div className="grid grid-cols-2">
            <div />
            <Button
              type="submit"
              className="button-rectangle mt-4 bg-gray-900 py-3 text-base text-white"
            >
              제출하기
            </Button>
          </div>
        </form>
      </div>
      <LoadingModal open={processingEnquiry} title={'문의 요청중'} />
    </>
  );
};
